import { PAYLINES_40 } from './paylines40.js';
import { PAYLINES_100 } from './paylines100.js';

/**
 * Which client build a launch `GameName` uses.
 *
 * Query keys stay as they are in production:
 * `GameName`, `PartnerId`, `Mode`, `OneTimeToken`, `Culture`, `Currency`, `LobbyUrl`.
 *
 * - Live 10-line titles send that same `GameName` to the hub.
 * - 40-line and 100-line titles keep our Ancient Egypt UI (both 5×4) but temporarily
 *   start as `CrownCrush40` / `CrownCrush100` until our own backends exist.
 * - Buy bonus opens only for an explicit buy-bonus `GameName`. Any other name is the
 *   regular 10-line game and is sent to the hub as itself.
 *
 * Layout mutation lives in {@link applyLaunchLayout} (`gameConfig.js`) so it always
 * updates the same `gameConfig` object GameApp imports (Vite HMR-safe).
 */
const LIVE_GAME_NAMES = ['AncientEgypt', 'AncientEgyptWilds'];

/**
 * Launcher / URL names for the 5×4 · 40-payline build.
 * `CrownCrush40` is accepted so we can hit the shared backend directly while testing.
 */
const LINES_40_GAME_NAMES = [
  'AncientEgypt40',
  'AncientEgyptWilds40',
  'CrownCrush40',
];

/**
 * Launcher / URL names for the 5×4 · 100-payline build.
 * `CrownCrush100` is accepted so we can hit the shared backend directly while testing.
 */
const LINES_100_GAME_NAMES = [
  'AncientEgypt100',
  'AncientEgyptWilds100',
  'CrownCrush100',
];

/** Launch names that open the buy-bonus client. Anything else is not buy bonus. */
const BUY_BONUS_GAME_NAMES = ['CrownCrushBB'];

/** StartGameAction GameName for the buy-bonus version. */
export const BUY_BONUS_BACKEND_GAME_NAME = 'CrownCrushBB';

/**
 * Temporary hub GameName for the 40-line build (Crown Crush's registered title).
 * Swap to our own name when the backend ships.
 */
export const LINES_40_BACKEND_GAME_NAME = 'CrownCrush40';

/**
 * Temporary hub GameName for the 100-line build (Crown Crush's registered title).
 * Swap to our own name when the backend ships.
 */
export const LINES_100_BACKEND_GAME_NAME = 'CrownCrush100';

/**
 * @param {string} name
 * @param {string[]} list
 */
function nameInList(name, list) {
  const folded = name.toLowerCase();
  return list.some((entry) => entry.toLowerCase() === folded);
}

/**
 * Translations for a 40- or 100-line launch come from the matching 10-line game.
 * Title and intro are client-owned (`localLineCopy.js`); every other string still uses that set.
 * @param {string} name
 * @returns {string}
 */
function wideLinesTranslationGameName(name) {
  const folded = name.toLowerCase();
  return folded === 'ancientegypt40' || folded === 'ancientegypt100'
    ? 'AncientEgypt'
    : 'AncientEgyptWilds';
}

/**
 * @param {string | null | undefined} gameName
 * @returns {{
 *   gameName: string | null,
 *   buyBonus: boolean,
 *   lines40: boolean,
 *   lines100: boolean,
 *   backendGameName: string | null,
 *   translationGameName?: string,
 *   rowCount: number,
 *   paylines: number[][] | null,
 * }}
 */
export function resolveGameVariant(gameName) {
  const name = String(gameName ?? '').trim();
  if (!name) {
    return {
      gameName: null,
      buyBonus: false,
      lines40: false,
      lines100: false,
      backendGameName: null,
      rowCount: 3,
      paylines: null,
    };
  }

  if (nameInList(name, LINES_100_GAME_NAMES)) {
    return {
      gameName: name,
      buyBonus: false,
      lines40: false,
      lines100: true,
      backendGameName: LINES_100_BACKEND_GAME_NAME,
      translationGameName: wideLinesTranslationGameName(name),
      rowCount: 4,
      paylines: PAYLINES_100,
    };
  }

  if (nameInList(name, LINES_40_GAME_NAMES)) {
    return {
      gameName: name,
      buyBonus: false,
      lines40: true,
      lines100: false,
      backendGameName: LINES_40_BACKEND_GAME_NAME,
      translationGameName: wideLinesTranslationGameName(name),
      rowCount: 4,
      paylines: PAYLINES_40,
    };
  }

  if (nameInList(name, BUY_BONUS_GAME_NAMES)) {
    return {
      gameName: name,
      buyBonus: true,
      lines40: false,
      lines100: false,
      backendGameName: BUY_BONUS_BACKEND_GAME_NAME,
      rowCount: 3,
      paylines: null,
    };
  }

  if (nameInList(name, LIVE_GAME_NAMES)) {
    return {
      gameName: name,
      buyBonus: false,
      lines40: false,
      lines100: false,
      backendGameName: name,
      rowCount: 3,
      paylines: null,
    };
  }

  return {
    gameName: name,
    buyBonus: false,
    lines40: false,
    lines100: false,
    backendGameName: name,
    rowCount: 3,
    paylines: null,
  };
}
